import { useEffect } from "react";

import {
  applySiteSwitchParams,
  resolveSiteSwitchParams,
} from "~/hec/utils/site-switch-params";

/** Marks the cross-store tab in the header (see `alert-bar.tsx`). */
export const TPA_SITE_SWITCH_ATTR = "data-tpa-site-switch";

/** Every event that can precede the browser reading the tab's href. */
const TPA_SITE_SWITCH_EVENTS = ["pointerdown", "contextmenu", "click"] as const;

/**
 * COM-661 — carry TPA attribution across the FSA <-> HSA tabbed experience.
 *
 * A shopper who arrives from a TPA partner gets an affiliate cookie on the
 * store they landed on. Clicking the other store's tab used to drop it: the
 * two storefronts are different registrable domains, so the browser cannot
 * share a cookie between them. The fix recreates SFCC-2690 — put the affiliate
 * details (`AFID` / `GroupName` / `CID`) on the outbound link, and let the
 * destination store write its own cookies (`tpaBranding` + `TPA`) through its
 * normal landing-page path. That is what satisfies the ticket's "NO changes to
 * logic on how we add cookie to browsers". Unlike SFCC-2690 it also carries the
 * legacy `a_aid` and `ParentID` journeys, which martech confirmed are still in
 * use — see `resolveSiteSwitchParams` in
 * `~/hec/utils/site-switch-params`.
 *
 * WHY AT CLICK TIME, NOT IN THE RENDERED HREF. The guest full-page HTML edge
 * cache (`WORKERS_GUEST_HTML_CACHE_MODE`, on for develop/preview/staging) means
 * a server-rendered href is shared by every guest who gets that cached
 * document. Baking one shopper's AFID into it would hand their affiliate
 * attribution to everyone else served the same page. Reading the cookie in a
 * click handler keeps the value per-shopper and always current. SFRA reached
 * the same conclusion — its final implementation is front-end only.
 *
 * WHY A DELEGATED LISTENER rather than rewriting hrefs on mount: it survives
 * re-renders, needs no DOM mutation on load, and keeps working when the tabs
 * are present but visually hidden (COM-197 suppresses them below `lg` on
 * non-home routes via CSS, so the anchors remain in the DOM and a click on a
 * VISIBLE tab is still decorated). The two tickets are therefore independent
 * and can merge in either order.
 *
 * Deliberately NOT gated on the `gtmRevShare` flag that gates
 * `RevShareLinkTracker`: that flag is about affiliate-link decoration owned by
 * GTM, and GTM has no tag for the site-switcher tabs. Gating here would mean
 * the transfer silently stopped on whichever sites have GTM handling
 * rev-share. Flagged for martech confirmation on COM-661; if they say
 * otherwise this becomes a one-line gate.
 *
 * WHICH EVENTS. SFRA rewrote the href on load, so every way of leaving
 * through the tab carried attribution. A `click` listener alone does not:
 * middle-click, "Open in new tab", "Copy link address" and dragging the link
 * out all read the href without a `click` ever firing. So the href is
 * decorated as early as the shopper commits to the link:
 * - `pointerdown` — any mouse button, touch (incl. the long-press menu), pen;
 *   runs before the browser reads the href for any of the above.
 * - `contextmenu` — the keyboard menu key / Shift+F10, which has no pointer.
 * - `click` — keyboard Enter / Ctrl+Enter on a focused tab.
 * Decorating is idempotent and reads the cookies fresh each time, so firing
 * more than once per navigation is harmless.
 */
export function TpaSiteSwitchTracker() {
  useEffect(() => {
    const decorate = (event: Event) => {
      const target = event.target as Element | null;
      const anchor = target?.closest<HTMLAnchorElement>(
        `a[${TPA_SITE_SWITCH_ATTR}]`,
      );
      if (!anchor) return;

      applySiteSwitchParams(anchor, resolveSiteSwitchParams());
    };

    for (const type of TPA_SITE_SWITCH_EVENTS) {
      document.addEventListener(type, decorate, true);
    }
    return () => {
      for (const type of TPA_SITE_SWITCH_EVENTS) {
        document.removeEventListener(type, decorate, true);
      }
    };
  }, []);

  return null;
}
